import { z } from "zod";

export const ThemeEnum = z.enum(["light", "dark"]);
export const FlowEnum = z.enum(["flex", "vision-ai", "retail-app"]);

export type Theme = z.infer<typeof ThemeEnum>;
export type Flow = z.infer<typeof FlowEnum>;

export const booleanFromString = z
	.union([z.boolean(), z.enum(["true", "false"]).transform((value) => value === "true")])
	.optional();

// Base search schema - includes params that can be passed to any
// route under /$locale/$partner, and are used during app initialization.
// Each sub-route can extend this with additional params as needed.
export const baseSearchSchema = z.object({
	// Session & Integration
	eii: z.string().optional(),
	sessionId: z.string().optional(),

	// UI/Theme
	theme: ThemeEnum.optional(),
	flow: FlowEnum.optional(),

	// Boolean flags - always available
	tablet: booleanFromString,
	webview: booleanFromString,
	disableCookies: booleanFromString,
});

export type BaseSearch = z.infer<typeof baseSearchSchema>;

// Device search params - used in device listing & details pages, and during onboarding
export const deviceSearchSchema = z.object({
	imei: z.string().optional(),
	brand: z.string().optional(),
	model: z.string().optional(),
	capacity: z.string().optional(),
	memory: z.string().optional(),
	tranType: z.string().optional(),
	crrPrgCode: z.string().optional(),
	email: z.email().optional(),
	locationId: z.string().optional(),
});

export type DeviceSearch = z.infer<typeof deviceSearchSchema>;

// Navigation params - used for screen overrides and redirection after flows
export const navigationSearchSchema = z.object({
	// Navigation override - jump to specific screen
	screen: z.string().optional(),
	// Return destination after completing a sub-flow
	returnTo: z.string().optional(),
	// Redirect configuration
	redirectStep: z.string().optional(),
	redirectUrl: z.string().optional(),
});

export type NavigationSearch = z.infer<typeof navigationSearchSchema>;

export const debugSearchSchema = z.object({
	debug: booleanFromString,
});

export type DebugSearch = z.infer<typeof debugSearchSchema>;

/**
 * Full init schema - Used at /$locale/$partner layout for initial app load.
 * Includes all params needed during app initialization.
 */
export const initSearchSchema = baseSearchSchema
	.extend(deviceSearchSchema.shape)
	.extend(navigationSearchSchema.shape)
	.extend(debugSearchSchema.shape);

export type InitSearch = z.infer<typeof initSearchSchema>;
export type InitSearchInput = z.input<typeof initSearchSchema>;
